import type { IconCategory } from '@warplan/shared';
import { ICON_CATALOG } from '../lib/icon-catalog.js';

export interface IconPaletteProps {
  category: IconCategory;
  selectedIconId?: string | undefined;
  onPick: (iconId: string) => void;
  variant?: 'grid' | 'submenu';
  className?: string;
  /** Icon IDs that are off-limits (greyed out, non-clickable). */
  forbiddenIconIds?: ReadonlySet<string> | undefined;
}

/**
 * Renders the icon grid for one category. Used by:
 *   - MapToolbar  fly-out (variant='grid')
 *   - MapContextMenu submenu (variant='submenu', adds chrome)
 *   - MapObjectPopover change-icon mode (variant='grid')
 */
export function IconPalette({
  category,
  selectedIconId,
  onPick,
  variant = 'grid',
  className,
  forbiddenIconIds,
}: IconPaletteProps): React.JSX.Element {
  const cat = ICON_CATALOG[category];
  const wrapperClass =
    variant === 'submenu'
      ? 'rounded border border-slate-700 bg-slate-900/95 shadow-xl'
      : '';
  return (
    <div
      role="listbox"
      aria-label={`${cat.label} icons`}
      className={`${wrapperClass} ${className ?? ''}`.trim()}
    >
      {/* grid-cols with a fixed cell size so the layout works inside absolute-positioned
          fly-outs and menus that have no explicit width. auto-fill so 1..N columns. */}
      <div className="grid gap-1 p-1" style={{ gridTemplateColumns: 'repeat(auto-fill, 2.5rem)', minWidth: 'calc(2 * 2.5rem + 0.25rem)' }}>
        {cat.icons.map((icon) => {
          const isSelected = icon.id === selectedIconId;
          const isForbidden = forbiddenIconIds?.has(icon.id) ?? false;
          return (
            <button
              key={icon.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              aria-disabled={isForbidden}
              title={icon.label}
              disabled={isForbidden}
              onClick={() => onPick(icon.id)}
              className={
                'flex h-10 w-10 items-center justify-center rounded transition ' +
                (isForbidden
                  ? 'opacity-30 cursor-not-allowed'
                  : isSelected
                    ? 'ring-2 ring-emerald-300 bg-slate-800'
                    : 'hover:bg-slate-700/70')
              }
            >
              <img
                src={icon.src}
                alt=""
                className="h-8 w-8 object-contain p-0.5"
                draggable={false}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
